import { clearRuntimePackPricing, setRuntimePackPricing } from './businessData'

export type PublicPricingDelivery = {
  currency: 'NZD'
  version: number
  packs: Array<{ quantity: 4 | 6; priceCents: number }>
  delivery: {
    bands: Array<{
      minExclusiveKm: number | null
      maxDistanceKm: 8 | 15 | 35
      feeCents: number
    }>
    maxDistanceKm: 35
  }
}

function isNonNegativeCents(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0
}

function parsePricingDelivery(value: unknown): PublicPricingDelivery {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('invalid-pricing-response')
  const record = value as Record<string, unknown>
  if (record.currency !== 'NZD' || !Number.isInteger(record.version) || Number(record.version) < 1) {
    throw new Error('invalid-pricing-response')
  }
  if (!Array.isArray(record.packs) || record.packs.length !== 2) throw new Error('invalid-pricing-response')
  const packs = record.packs.map((raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('invalid-pricing-response')
    const pack = raw as Record<string, unknown>
    const quantity = Number(pack.quantity)
    const priceCents = Number(pack.priceCents)
    if ((quantity !== 4 && quantity !== 6) || !Number.isInteger(priceCents) || priceCents <= 0) {
      throw new Error('invalid-pricing-response')
    }
    return { quantity: quantity as 4 | 6, priceCents }
  })
  if (new Set(packs.map((pack) => pack.quantity)).size !== 2) throw new Error('invalid-pricing-response')

  if (!record.delivery || typeof record.delivery !== 'object' || Array.isArray(record.delivery)) throw new Error('invalid-pricing-response')
  const delivery = record.delivery as Record<string, unknown>
  if (delivery.maxDistanceKm !== 35 || !Array.isArray(delivery.bands) || delivery.bands.length !== 3) {
    throw new Error('invalid-pricing-response')
  }
  const expected = [8, 15, 35] as const
  const bands = delivery.bands.map((raw, index) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('invalid-pricing-response')
    const band = raw as Record<string, unknown>
    const maxDistanceKm = Number(band.maxDistanceKm)
    const feeCents = Number(band.feeCents)
    const minExclusiveKm = band.minExclusiveKm === null ? null : Number(band.minExclusiveKm)
    if (maxDistanceKm !== expected[index] || !isNonNegativeCents(feeCents)) throw new Error('invalid-pricing-response')
    if (index === 0 ? minExclusiveKm !== null : minExclusiveKm !== expected[index - 1]) throw new Error('invalid-pricing-response')
    return { minExclusiveKm, maxDistanceKm: expected[index], feeCents }
  })

  return {
    currency: 'NZD',
    version: Number(record.version),
    packs,
    delivery: { bands, maxDistanceKm: 35 },
  }
}

export async function fetchPricingDelivery(): Promise<PublicPricingDelivery> {
  const response = await fetch('/api/pricing-delivery', {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error('pricing-unavailable')
  return parsePricingDelivery(await response.json())
}

export function installPricingDelivery(pricing: PublicPricingDelivery) {
  setRuntimePackPricing(pricing.packs)
}

export function clearPricingDelivery() {
  clearRuntimePackPricing()
}
